import firebase from 'firebase';
import { v4 as uuid } from 'uuid';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { serialize, deserialize } from '../../utils/db';

const MODEL = 'videos';
const DEFAULT_CACHE_DURATION = 10 * 60 * 1000;

const cache = {};

export function search(q, callback, params = { model: MODEL }) {
  const db = firebase.firestore();
  const { subscribe } = params;
  const model = params.model || MODEL;

  const query = q || db.collection(model);

  if (subscribe) {
    return query.onSnapshot(
      snapshot => {
        const results = snapshot.docs.map(doc => {
          return deserialize(doc.exists ? doc.data() : null);
        });
        callback(results.filter(i => i.id));
      },
      error => {
        console.warn('[ERROR]', error);
        callback([]);
      },
    );
  } else {
    return query
      .get()
      .then(snapshot => {
        const results = snapshot.docs.map(doc => {
          return deserialize(doc.exists ? doc.data() : null);
        });
        callback(results.filter(i => i.id));
      })
      .catch(error => {
        console.warn('[ERROR]', error);
        callback([]);
      });
  }
}

export function list(callback, params = { model: MODEL }) {
  return search(null, callback, params);
}

export function get(id, callback, params = { model: MODEL }) {
  const { subscribe } = params;
  const db = firebase.firestore();
  const model = params.model || MODEL;

  if (subscribe) {
    return db
      .collection(model)
      .doc(id)
      .onSnapshot(
        doc => {
          const result = deserialize(doc.exists ? doc.data() : null);
          callback(result);
        },
        error => {
          console.warn('[ERROR]', error);
          callback(null);
        },
      );
  } else {
    return db
      .collection(model)
      .doc(id)
      .get()
      .then(doc => {
        const result = deserialize(doc.exists ? doc.data() : null);
        callback(result);
      })
      .catch(error => {
        console.warn('[ERROR]', error);
        callback(null);
      });
  }
}

export function getByIds(ids, callback, params = { model: MODEL }) {
  const db = firebase.firestore();
  const { useCache = true, refreshCache = false, cacheDuration = DEFAULT_CACHE_DURATION } = params;
  const model = params.model || MODEL;

  const idsToFetch = useCache ? ids.filter(id => {
    const cachedData = cache[id];
    return refreshCache || !cachedData || !cachedData.cacheTime || Date.now() - cachedData.cacheTime > cacheDuration;
  }) : ids;

  if (isEmpty(idsToFetch)) {
    callback(ids.map(id => cache[id].data));
    return;
  }

  db
    .collection(model)
    .where('id', 'in', idsToFetch)
    .get()
    .then(snapshot => {
      const results = snapshot.docs.map(doc => {
        return deserialize(doc.exists ? doc.data() : null);
      }).filter(i => i.id);
      results.forEach(i => {
        cache[i.id] = { cacheTime: Date.now(), data: i };
      });
      callback(ids.map(id => cache[id].data));
    })
    .catch(error => {
      console.warn('[ERROR]', error);
      callback([]);
    });
}

export function create(entity, id = uuid(), params = { model: MODEL }) {
  const db = firebase.firestore();
  const model = params.model || MODEL;
  const data = {
    ...entity,
    id,
    createdAt: moment(),
  };

  const ref = db
    .collection(model)
    .doc(id);

  return ref.set(serialize(data));
}

export function update(entity, params = { model: MODEL }) {
  const db = firebase.firestore();
  const { model } = params;

  const data = {
    ...entity,
    updatedAt: moment(),
  };

  delete data.recipient;
  delete data.sender;

  const ref = db
    .collection(model)
    .doc(entity.id);

  return ref.update(serialize(data));
}

export function set(entity, params = { model: MODEL }) {
  const db = firebase.firestore();
  const { model } = params;

  if (entity.id) {
    return db
      .collection(model)
      .doc(entity.id)
      .get()
      .then(doc => {
        if (doc.exists) {
          return update(entity);
        } else {
          return create(entity, entity.id);
        }
      });
  } else {
    return create(entity, params);
  }
}

export function deleteFolderContents(path) {
  return new Promise((resolve, reject) => {
    const ref = firebase.storage().ref(path);
    const deleteTasks = [];
    ref
      .listAll()
      .then(dir => {
        dir.items.forEach(fileRef => {
          deleteTasks.push(deleteFile(ref.fullPath, fileRef.name));
        });
        dir.prefixes.forEach(folderRef => {
          deleteTasks.push(deleteFolderContents(folderRef.fullPath));
        })
        Promise.all(deleteTasks).finally(resolve);
      })
      .catch(reject);
  });
}

export function deleteFile(pathToFile, fileName) {
  const ref = firebase.storage().ref(pathToFile);
  const childRef = ref.child(fileName);
  return childRef.delete();
}

export function remove(id, params = { model: MODEL }) {
  const db = firebase.firestore();
  const model = params.model || MODEL;

  return new Promise((resolve, reject) => {
    deleteFolderContents(`videos/${id}`)
      .finally(() => {
        db
          .collection(model)
          .doc(id)
          .delete()
          .then(resolve)
          .catch(reject);
      });
  });
}

export function upload(file, filename, progressCallback, path = 'videos/', customMetadata = null) {
  const { type } = file;
  const storage = firebase.storage();
  const storageRef = storage.ref();
  const finalMetadata = {
    contentType: type,
    customMetadata: {
      ...(customMetadata || {}),
      ownerId: (firebase.auth().currentUser || {}).uid,
    },
  };

  var uploadTask = storageRef.child(`${path}${filename}`).put(file, finalMetadata);

  return new Promise((resolve, reject) => {
    uploadTask.on('state_changed', (snapshot) => {
      const progress = snapshot.bytesTransferred / snapshot.totalBytes;
      progressCallback && progressCallback({
        progress,
        status: snapshot.state,
      });
    }, reject, () => {
      uploadTask.snapshot.ref.getDownloadURL().then(resolve);
    });
  });
}

export function videoModel(model) {
  return {
    create: (entity, id, params) => create(entity, id, { ...params, model }),
    update: (entity, params) => update(entity, { ...params, model }),
    set: (entity, params) => set(entity, { ...params, model }),
    get: (id, callback, params) => get(id, callback, (params = { ...params, model })),
    getByIds: (ids, callback, params) => getByIds(ids, callback, (params = { ...params, model })),
    search: (q, callback, params) => search(q, callback, { ...params, model }),
    list: (callback, params) => list(callback, { ...params, model }),
    upload: (file, filename, progressCallback, path, metadata) => upload(file, filename, progressCallback, path, metadata),
    remove: (id, params) => remove(id, { ...params, model }),
  };
}

export default {
  create,
  update,
  set,
  get,
  getByIds,
  search,
  list,
  upload,
  remove,
};
